// src/components/ClientPortalResetPassword.js
// Public forgot/reset password screen for the client portal.
// Two modes on one route:
//   /client-portal-reset                 -> request a reset link by email
//   /client-portal-reset?email=&token=   -> set a new password (from the email link)

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { KeyRound, Mail, Lock, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const Spin = () => (
  <svg
    className="animate-spin -ml-1 mr-3 h-5 w-5"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    ></path>
  </svg>
);

const inputClass =
  'appearance-none block w-full px-3.5 py-2.5 rounded-md text-sm border border-grayteck-300 dark:border-darkbg-500 bg-white dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100 placeholder:text-grayteck-400 dark:placeholder:text-grayteck-500 focus:outline-none focus:ring-2 focus:ring-grayteck-600 dark:focus:ring-gold-400 focus:border-transparent transition-all duration-200';

const labelClass =
  'block text-xs font-bold uppercase tracking-caps text-grayteck-700 dark:text-grayteck-400 mb-1.5';

const primaryBtnClass =
  'group relative w-full flex justify-center items-center py-2.5 px-4 text-sm font-bold rounded-md text-accentfg bg-gold-400 hover:bg-gold-300 active:bg-gold-500 shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const ClientPortalResetPassword = () => {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromLink = searchParams.get('token') || '';
  const emailFromLink = searchParams.get('email') || '';
  const isResetMode = Boolean(tokenFromLink);

  const { forgotPassword, isRequestingReset, resetPassword, isResettingPassword } =
    useAuth();

  const [requestSent, setRequestSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { email: emailFromLink },
  });

  const onSubmitRequest = async (data) => {
    try {
      await forgotPassword.mutateAsync({ email: data.email });
      setRequestSent(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
    }
  };

  const onSubmitReset = async (data) => {
    try {
      await resetPassword.mutateAsync({
        email: data.email,
        token: tokenFromLink,
        newPassword: data.newPassword,
      });
      setResetDone(true);
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-grayteck-50 dark:bg-darkbg-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-sm w-full">
        {/* Brand */}
        <div className="text-center mb-10">
          <span className="font-display text-2xl font-bold tracking-tight text-grayteck-900 dark:text-grayteck-100">
            GrayTeck<span className="text-gold-400">.</span>
          </span>
          <span className="block mt-1 text-[11px] font-semibold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
            Client Portal
          </span>
        </div>

        <div className="flex items-center gap-2.5 mb-1.5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-grayteck-100 dark:bg-darkbg-700">
            <KeyRound className="w-4 h-4 text-grayteck-600 dark:text-grayteck-300" />
          </span>
          <h2 className="font-display text-2xl font-bold text-grayteck-900 dark:text-grayteck-100 tracking-tight">
            {isResetMode ? 'Choose a new password' : 'Reset your password'}
          </h2>
        </div>
        <p className="mb-8 text-sm text-grayteck-700 dark:text-grayteck-400">
          {isResetMode
            ? 'Enter a new password for your portal account.'
            : 'Enter your email and we will send you a password reset link.'}
        </p>

        {resetDone ? (
          /* Reset complete */
          <div className="space-y-5">
            <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/25 border border-emerald-200 dark:border-emerald-400/20 p-3.5 flex items-start gap-2.5">
              <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <p className="text-sm text-emerald-800 dark:text-emerald-300">
                Your password has been reset. You can now sign in with your new
                password.
              </p>
            </div>
            <button
              type="button"
              onClick={() => nav('/client-portal-login', { replace: true })}
              className={primaryBtnClass}
            >
              Go to Sign In
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        ) : requestSent ? (
          /* Request confirmation (always generic — server never reveals whether the email exists) */
          <div className="space-y-5">
            <div className="rounded-md bg-grayteck-100 dark:bg-darkbg-700 border border-grayteck-200 dark:border-darkbg-600 px-3.5 py-3">
              <p className="text-sm text-grayteck-700 dark:text-grayteck-300">
                If an account with that email exists, a password reset link is on
                its way. The link expires in 30 minutes — check your inbox.
              </p>
            </div>
            <Link
              to="/client-portal-login"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-gold-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </div>
        ) : isResetMode ? (
          /* Set new password (from emailed link) */
          <form className="space-y-5" onSubmit={handleSubmit(onSubmitReset)}>
            <div>
              <label htmlFor="email" className={labelClass}>Email</label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  {...register('email', { required: 'Email is required' })}
                  className={`${inputClass} pl-10`}
                  placeholder="you@company.com"
                  disabled={isResettingPassword}
                />
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="newPassword" className={labelClass}>New Password</label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  {...register('newPassword', {
                    required: 'New password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters long',
                    },
                  })}
                  className={`${inputClass} pl-10`}
                  placeholder="At least 8 characters"
                  disabled={isResettingPassword}
                />
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
              </div>
              {errors.newPassword && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.newPassword.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className={labelClass}>Confirm New Password</label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your new password',
                    validate: (value) =>
                      value === watch('newPassword') || 'Passwords do not match',
                  })}
                  className={`${inputClass} pl-10`}
                  placeholder="Re-enter your new password"
                  disabled={isResettingPassword}
                />
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>

            {resetPassword.error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-400/20 p-3.5">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                  {resetPassword.error.detail ||
                    'Failed to reset password. The link may have expired — request a new one.'}
                </h3>
              </div>
            )}

            <button
              type="submit"
              disabled={isResettingPassword}
              className={primaryBtnClass}
            >
              {isResettingPassword ? (
                <>
                  <Spin />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>

            <p className="text-center">
              <Link
                to="/client-portal-reset"
                className="text-xs font-semibold text-grayteck-500 dark:text-grayteck-500 hover:text-grayteck-800 dark:hover:text-grayteck-300 transition-colors"
              >
                Link expired? Request a new one
              </Link>
            </p>
          </form>
        ) : (
          /* Request a reset link */
          <form className="space-y-5" onSubmit={handleSubmit(onSubmitRequest)}>
            <div>
              <label htmlFor="email" className={labelClass}>Email</label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  {...register('email', { required: 'Email is required' })}
                  className={`${inputClass} pl-10`}
                  placeholder="you@company.com"
                  disabled={isRequestingReset}
                />
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-400" />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isRequestingReset}
              className={primaryBtnClass}
            >
              {isRequestingReset ? (
                <>
                  <Spin />
                  Sending...
                </>
              ) : (
                <>
                  Send Reset Link
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            <p className="text-center">
              <Link
                to="/client-portal-login"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-gold-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Link>
            </p>
          </form>
        )}

        <p className="mt-10 text-center text-xs text-grayteck-500 dark:text-grayteck-500">
          © {new Date().getFullYear()} GrayTecknologies LLC
        </p>
      </div>
    </div>
  );
};

export default ClientPortalResetPassword;
